/**
 * hooks/useOnboardingDismissal.ts
 *
 * Computes whether the profile onboarding card should be shown on the Dashboard,
 * and exposes a method to dismiss it permanently.
 *
 * The card is shown when:
 *   - onboarding_dismissed = false (user has not manually dismissed it)
 *   - AND at least one of logo_url or stamp_title is missing
 *
 * Dismissal is written to users.onboarding_dismissed = true in the DB,
 * then refreshProfile() syncs the AuthContext so the card disappears immediately.
 *
 * Auto-dismissal (both logo and stamp now set) is handled in useSettingsProfile.saveProfile.
 *
 * Created Apr 2026: part of the onboarding nudge feature for the Dashboard.
 * Updated Apr 2026: split DB update and refreshProfile into separate try/catch
 *   blocks so a refresh failure doesn't mask a successful DB write.
 * Updated Apr 2026: stampTitle is now treated as missing when it is whitespace-only,
 *   matching the TRIM check in the corrective DB migration.
 */

import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { captureError } from '../utils/errorReporting';

export function useOnboardingDismissal() {
  const { user, userProfile, refreshProfile } = useAuth();
  const [dismissing, setDismissing] = useState(false);

  const shouldShow =
    !!userProfile &&
    !userProfile.onboardingDismissed &&
    (!userProfile.logoUrl || (userProfile.stampTitle ?? '').trim() === '');

  const dismissOnboarding = useCallback(async () => {
    if (!user || dismissing) return;
    setDismissing(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ onboarding_dismissed: true })
        .eq('id', user.id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to dismiss onboarding:', err);
      captureError(err, { hook: 'useOnboardingDismissal', action: 'dismissOnboarding', userId: user.id });
      setDismissing(false);
      return;
    }

    try {
      await refreshProfile();
    } catch (err) {
      console.error('refreshProfile failed after onboarding dismissal:', err);
      captureError(err, { hook: 'useOnboardingDismissal', action: 'refreshProfile', userId: user.id });
    } finally {
      setDismissing(false);
    }
  }, [user, dismissing, refreshProfile]);

  return { shouldShow, dismissOnboarding, dismissing };
}
